import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PrimeReactProvider } from 'primereact/api';

// PrimeReact CSS
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";

import './index.css';

import {
  LoadingFallback,
  PermissionRoute,
  ProtectedRoute,
} from './app/routes/RouteGuards';
import AppLayout from './components/layout/AppLayout';
import { AuthProvider } from './context/AuthContext';
import { WebSocketProvider } from './context/WebSocketProvider';
import { ToastProvider } from './context/ToastProvider';

const LoginPage = React.lazy(() =>
  import('./modules/auth/pages/LoginPage')
    .then(m => ({ default: m.LoginPage }))
);
const UsersListPage = React.lazy(() =>
  import('./modules/users/pages/UsersListPage')
    .then(m => ({ default: m.UsersListPage }))
);
const RolesListPage = React.lazy(() =>
  import('./modules/roles/pages/RolesListPage')
    .then(m => ({ default: m.RolesListPage }))
);
const DevicesListPage = React.lazy(() =>
  import('./modules/devices/pages/DevicesListPage')
    .then(m => ({ default: m.DevicesListPage }))
);
const DeviceCallPage = React.lazy(() =>
  import('./modules/call/pages/DeviceCallPage')
    .then(m => ({ default: m.DeviceCallPage }))
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000 },
    mutations: { retry: 1 },
  },
});

createRoot(document.getElementById('root')!).render(
  <PrimeReactProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
        <WebSocketProvider>
          <BrowserRouter>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
              {/* Public — no layout */}
              <Route path="/login" element={<LoginPage />} />

              {/* Call page — no layout chrome */}
              <Route path="/call/:deviceId" element={<DeviceCallPage />} />

              {/* Protected — inside AppLayout */}
              <Route element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }>
                <Route index element={<Navigate to="/devices" replace />} />
                <Route path="/devices" element={<DevicesListPage />} />
                <Route path="/users" element={
                  <PermissionRoute module="users">
                    <UsersListPage />
                  </PermissionRoute>
                } />
                <Route path="/roles" element={
                  <PermissionRoute module="roles">
                    <RolesListPage />
                  </PermissionRoute>
                } />
                <Route path="*" element={<Navigate to="/devices" replace />} />
              </Route>
            </Routes>
          </Suspense>
          <ReactQueryDevtools initialIsOpen={false} />
        </BrowserRouter>
      </WebSocketProvider>
      </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  </PrimeReactProvider>
);
